/**
 * EscalateDialog — high-stakes, NON-OPTIMISTIC escalation.
 *
 * Escalation re-routes the request to a human and is irreversible from the UI,
 * so we AWAIT the backend (`mutateAsync`) before closing the dialog. The board
 * only updates after the server confirms; on failure the dialog stays open and
 * shows an error. The trigger and confirm controls carry stable data-testids.
 */

import type { ServiceRequest } from '@hostesa/api-client';
import { useEscalateTask } from '@hostesa/api-client';
import { useT } from '@hostesa/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Label,
  Textarea,
} from '@hostesa/ui';
import { useState } from 'react';
import { asOptionalString } from './task-presentation';

export interface EscalateDialogProps {
  task: ServiceRequest;
  tenantId: string;
  hotelId: string;
}

export function EscalateDialog({ task, tenantId, hotelId }: EscalateDialogProps) {
  const { t } = useT('operations');
  const mutation = useEscalateTask(tenantId, hotelId);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [failed, setFailed] = useState(false);

  const room = asOptionalString(task.room) ?? t('card.roomUnconfirmed');
  const category = t(`category.${task.category}`);

  const onOpenChange = (next: boolean) => {
    // Never close while the backend call is in flight (non-optimistic).
    if (mutation.isPending) return;
    setOpen(next);
    if (!next) {
      setFailed(false);
      setReason('');
    }
  };

  const onConfirm = async () => {
    setFailed(false);
    try {
      const trimmed = reason.trim();
      // AWAIT the authoritative server result before the board updates.
      await mutation.mutateAsync({
        requestId: task.id,
        body: trimmed.length > 0 ? { reason: trimmed } : undefined,
      });
      setOpen(false);
      setReason('');
    } catch {
      setFailed(true);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="destructive"
          aria-label={`${t('actions.escalate')} — ${room}, ${category}`}
          data-testid={`task-escalate-btn-${task.id}`}
        >
          {t('actions.escalate')}
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="task-escalate-dialog">
        <DialogHeader>
          <DialogTitle>{t('escalate.title')}</DialogTitle>
          <DialogDescription>{t('escalate.description')}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`escalate-reason-${task.id}`}>{t('escalate.reasonLabel')}</Label>
          <Textarea
            id={`escalate-reason-${task.id}`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('escalate.reasonPlaceholder')}
            disabled={mutation.isPending}
            data-testid="task-escalate-reason-input"
          />
        </div>

        {failed && (
          <p
            role="alert"
            className="text-sm text-[var(--color-alert)]"
            data-testid="task-escalate-error"
          >
            {t('escalate.error')}
          </p>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            disabled={mutation.isPending}
            onClick={() => onOpenChange(false)}
            data-testid="task-escalate-cancel-btn"
          >
            {t('escalate.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={mutation.isPending}
            onClick={onConfirm}
            data-testid="task-escalate-confirm-btn"
          >
            {mutation.isPending ? t('actions.escalating') : t('escalate.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
